/**
 * Detalhe do pedido unificado (Task 12, brief 3.3).
 *
 * Reúne, para o MESMO registro, as etapas que o brief pede — pagamento,
 * separação/expedição, transporte, faturamento fiscal, cancelamento e
 * devolução — e a origem de onde o pedido veio.
 *
 * Regras que a tela respeita:
 * - **origem navegável**: conversa do atendimento, pedido do checkout ou
 *   identificador do canal externo; nunca só o rótulo do canal;
 * - **cliente canônico**: o pedido aponta para o mesmo `customers` (0020) que
 *   as conversas e o CRM usam (link para o Customer 360);
 * - **nada inventado**: campo ainda não informado aparece como "não informado"
 *   com o motivo, e não como vazio ou zero;
 * - **fiscal não é emitido aqui** (D-12.3): exibimos a REFERÊNCIA da NF do
 *   Bling. A Plataforma não emite documento fiscal;
 * - **a máquina de estados decide o que é possível**: os próximos passos vêm
 *   do módulo puro, não de uma lista de botões desta tela.
 *
 * Migrado para `.tsx`/JSX real (Cluster A — Vendas, redesign por perfil; ver
 * `docs/architecture/ADR-0011-*.md`). Mudança de sintaxe (`createElement` →
 * JSX) + um ícone lucide por bloco (`SectionTitle`), no mesmo padrão visual
 * de `KpiCard.tsx` (ícone em caixa colorida ao lado do título). Puramente
 * decorativo — nenhum `data-*`/texto/rota muda.
 */
// Import explícito de `React` — ver o mesmo comentário em `Margens.tsx`.
import React, { useState, type ReactElement, type ReactNode } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Ban, Package, Receipt, Signpost, Truck, Wallet, type LucideIcon } from "lucide-react";
import { Badge, type BadgeTone } from "../ui/index.js";
import { RecordTable } from "../components/RecordTable.js";
import { OrderStateMachine } from "../components/OrderStateMachine.js";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { PendingField } from "../components/PendingField.js";
import { isOrdersSourceAwaitingContract, originDrillDown, unifiedOrderById } from "../orders/dataset.js";
import { ORDER_ORIGIN_LABELS, ORDER_STATE_LABELS, type PlatformOrderState } from "../orders/state-machine.js";
import { useOrder, useOrderTransition, useSalesOrigins, useSetOrderAcquisition } from "../queries.js";
import { ACQUISITION_SOURCE_LABELS, acquisitionOriginLabel } from "../orders/acquisition.js";
import { AWAITING_CONTRACT_LABEL } from "../dashboard/kpis.js";
import { formatBrl, formatDateTime } from "../format.js";
import type { OrdersDataset } from "../orders/dataset.js";
import type { OrderAcquisitionHandle, OrderTransitionHandle, PedidoDetalheViewProps } from "../orders/view-props.js";
import type { OrderCartItem, OrderPayment } from "../../../../packages/checkout/src/index.js";
import type { PlatformUnifiedOrder } from "../api/types.js";

const DETAIL_STATE_TONE: Record<PlatformOrderState, BadgeTone> = {
  payment_pending: "warning",
  paid: "info",
  separation: "info",
  shipping: "info",
  invoiced: "info",
  delivered: "success",
  cancelled: "danger",
  returned: "danger"
};

/** Valor ausente nunca vira vazio mudo: a tela diz por que não há valor. */
function pending(label: string): ReactElement {
  return <PendingField label={label} />;
}

/** Casca de seção do admin de referência: cartão compacto com título e lista de pares rótulo/valor. */
const BLOCK_CLASS = "rounded-panel border border-border bg-surface p-5 shadow-s1 flex flex-col gap-3";

function SectionTitle({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-center gap-2">
      <Icon className="h-5 w-5 shrink-0 text-muted" aria-hidden focusable={false} />
      <h3 className="text-lg font-extrabold text-ink">{children}</h3>
    </div>
  );
}

/** Linha rótulo → valor (`dl`): o valor ausente é dito, nunca fica em branco. */
function Row({ label, children }: { label: string; children: ReactNode }): ReactElement {
  return (
    <div className="flex items-start justify-between gap-4 border-b border-border py-2 text-sm last:border-b-0">
      <dt className="shrink-0 text-muted">{label}</dt>
      <dd className="m-0 min-w-0 break-words text-right font-bold text-ink">{children}</dd>
    </div>
  );
}

function OriginBlock({ order }: { order: PlatformUnifiedOrder }): ReactElement {
  const drill = originDrillDown(order);
  return (
    <section className={`order-detail__origin ${BLOCK_CLASS}`} data-section="origem">
      <SectionTitle icon={Signpost}>Origem e cliente</SectionTitle>
      <dl className="m-0" data-order-origin={order.origin}>
        <Row label="Canal">{ORDER_ORIGIN_LABELS[order.origin]}</Row>
        <Row label="Identificador no canal">
          <span data-order-external-id={order.externalId}>{order.externalId}</span>
        </Row>
        <Row label="Registro de origem">
          {/* `data-origin-link` no wrapper: as props de `Link` não aceitam
              atributos arbitrários no tipo do react-router. */}
          <span data-origin-link={drill.route ?? "none"}>
            {drill.route ? (
              <Link to={drill.route} className="text-brand-text underline">
                {drill.label}
              </Link>
            ) : (
              drill.label
            )}
          </span>
        </Row>
        <Row label="Cliente">
          {order.customerId ? (
            <span data-customer-id={order.customerId}>
              <Link to={`/clientes/${order.customerId}`} className="text-brand-text underline">
                {order.customer?.name ?? order.customerId}
              </Link>
            </span>
          ) : (
            <span className="font-semibold">
              {order.customer?.name ?? "—"} {pending("sem cadastro vinculado no CRM")}
            </span>
          )}
        </Row>
      </dl>
    </section>
  );
}

/**
 * Origem de aquisição (0062): de onde o contato veio, a estratégia/campanha e quem fechou.
 * Pedido importado do Bling chega sem origem — aqui a equipe marca. Sem `acquisition` a
 * tela só lê. Mostra COMO a origem foi definida (equipe, anúncio, link) para não misturar
 * fato detectado com declaração humana.
 */
function AcquisitionBlock({ order, acquisition }: { order: PlatformUnifiedOrder; acquisition?: OrderAcquisitionHandle }): ReactElement {
  const [origin, setOrigin] = useState(order.acquisitionOrigin ?? "");
  const [strategy, setStrategy] = useState(order.acquisitionStrategy ?? "");
  const [responsible, setResponsible] = useState(order.responsibleName ?? "");
  const options = acquisition?.origins ?? [];
  // Origem já gravada que foi desativada continua selecionável para não "sumir" do select.
  const choices = options.filter((option) => option.active || option.slug === order.acquisitionOrigin);
  const controlCls = "min-h-[44px] rounded-control border border-border bg-surface px-3";
  return (
    <section className={`order-detail__acquisition ${BLOCK_CLASS}`} data-section="origem-aquisicao">
      <SectionTitle icon={Signpost}>Origem da venda</SectionTitle>
      <dl className="m-0">
        <Row label="De onde veio">
          <span data-acquisition-origin={order.acquisitionOrigin ?? "none"}>
            {order.acquisitionOrigin ? acquisitionOriginLabel(order.acquisitionOrigin, options) : <span className="font-semibold">{pending("origem ainda não informada")}</span>}
          </span>
        </Row>
        {order.acquisitionSource ? <Row label="Como foi definida">{ACQUISITION_SOURCE_LABELS[order.acquisitionSource]}</Row> : null}
        {order.acquisitionStrategy ? <Row label="Estratégia">{order.acquisitionStrategy}</Row> : null}
        {order.responsibleName ? <Row label="Responsável">{order.responsibleName}</Row> : null}
      </dl>
      {acquisition ? (
        <form
          className="mt-3 flex flex-col gap-2"
          data-acquisition-form
          onSubmit={(event) => {
            event.preventDefault();
            if (origin) acquisition.save({ origin, strategy: strategy.trim() || undefined, responsibleName: responsible.trim() || undefined });
          }}
        >
          <label className="flex flex-col gap-1 text-sm font-semibold text-ink">
            Origem do contato
            <select className={controlCls} value={origin} data-acquisition-field="origin" onChange={(e) => setOrigin(e.target.value)}>
              <option value="">Selecione…</option>
              {choices.map((option) => (
                <option key={option.slug} value={option.slug}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-sm font-semibold text-ink">
            Estratégia ou campanha (opcional)
            <input className={controlCls} value={strategy} maxLength={200} data-acquisition-field="strategy" onChange={(e) => setStrategy(e.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-sm font-semibold text-ink">
            Responsável (opcional)
            <input className={controlCls} value={responsible} maxLength={120} data-acquisition-field="responsible" onChange={(e) => setResponsible(e.target.value)} />
          </label>
          <button type="submit" disabled={!origin || acquisition.pending} className="min-h-[44px] rounded-control bg-brand px-4 font-bold text-on-brand hover:bg-brand-pressed disabled:opacity-60" data-acquisition-action="salvar">
            {acquisition.pending ? "Salvando…" : "Salvar origem"}
          </button>
          {acquisition.error ? (
            <p role="alert" className="m-0 text-sm font-semibold text-danger" data-acquisition="erro">
              {errorDetail(acquisition.error)}
            </p>
          ) : null}
          {acquisition.saved && !acquisition.error ? (
            <p role="status" className="m-0 text-sm font-semibold text-success" data-acquisition="salvo">
              Origem salva.
            </p>
          ) : null}
        </form>
      ) : null}
    </section>
  );
}

function PaymentBlock({ order }: { order: PlatformUnifiedOrder }): ReactElement {
  return (
    <section className={`order-detail__payment ${BLOCK_CLASS}`} data-section="pagamento">
      <SectionTitle icon={Wallet}>Pagamento</SectionTitle>
      <dl className="m-0">
        <Row label="Subtotal">{formatBrl(order.subtotalCents)}</Row>
        <Row label="Frete">{order.shippingCents === null ? pending("sem cotação aprovada") : formatBrl(order.shippingCents)}</Row>
        <Row label="Total">
          <span className="text-base font-black">
            {order.totalCents === null ? pending("aguardando cotação de frete") : formatBrl(order.totalCents)}
          </span>
        </Row>
      </dl>
      {order.payments.length ? (
        <RecordTable<OrderPayment>
          caption="Pagamentos registrados no pedido de origem"
          rows={order.payments}
          idOf={(payment) => payment.id}
          emptyMessage="Nenhum pagamento registrado."
          columns={[
            { key: "id", label: "Pagamento", render: (payment) => payment.id },
            { key: "method", label: "Meio", render: (payment) => payment.method },
            { key: "amount", label: "Valor", render: (payment) => formatBrl(payment.amountCents) },
            { key: "status", label: "Situação", render: (payment) => payment.status },
            { key: "detail", label: "Detalhe", render: (payment) => payment.detail }
          ]}
        />
      ) : (
        <p className="m-0 text-xs text-muted" data-payments="0">
          Nenhum pagamento do provedor vinculado. A referência do pagamento aceito fica no histórico do pedido.
        </p>
      )}
    </section>
  );
}

function FulfillmentBlock({ order }: { order: PlatformUnifiedOrder }): ReactElement {
  return (
    <section className={`order-detail__fulfillment ${BLOCK_CLASS}`} data-section="expedicao">
      <SectionTitle icon={Truck}>Envio</SectionTitle>
      <dl className="m-0">
        <Row label="Transportadora">
          <span data-carrier={order.carrier ?? ""}>{order.carrier ?? pending("informada no despacho")}</span>
        </Row>
        <Row label="Rastreio">
          <span data-tracking-code={order.trackingCode ?? ""}>{order.trackingCode ?? pending("informado no despacho")}</span>
        </Row>
      </dl>
    </section>
  );
}

function FiscalBlock({ order }: { order: PlatformUnifiedOrder }): ReactElement {
  return (
    <section className={`order-detail__fiscal ${BLOCK_CLASS}`} data-section="faturamento">
      <SectionTitle icon={Receipt}>Faturamento fiscal</SectionTitle>
      <dl className="m-0">
        <Row label="NF (Bling)">
          <span data-nf-reference={order.nfReference ?? ""}>{order.nfReference ?? pending("registrada quando o Bling fatura")}</span>
        </Row>
      </dl>
      <p className="m-0 text-xs text-muted">O documento fiscal é emitido no Bling; a Plataforma guarda só a referência.</p>
    </section>
  );
}

function ClosureBlock({ order }: { order: PlatformUnifiedOrder }): ReactElement | null {
  if (!order.cancellationReason && !order.returnReason) return null;
  return (
    <section className={`order-detail__closure ${BLOCK_CLASS} border-danger`} data-section="encerramento">
      <SectionTitle icon={Ban}>Cancelamento / devolução</SectionTitle>
      {order.cancellationReason ? (
        <p className="m-0 text-sm" data-cancellation-reason={order.cancellationReason}>{`Cancelado: ${order.cancellationReason}`}</p>
      ) : null}
      {order.returnReason ? <p className="m-0 text-sm" data-return-reason={order.returnReason}>{`Devolvido: ${order.returnReason}`}</p> : null}
    </section>
  );
}

/** Texto seguro para exibir um campo qualquer do dado bruto do canal. */
function show(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  return JSON.stringify(value);
}

function brlFrom(value: unknown): string {
  return typeof value === "number" ? formatBrl(Math.round(value * 100)) : "—";
}

/**
 * Tudo que o canal entregou sobre o pedido, guardado no Supabase (`bling_raw`): quem vendeu, taxas e
 * comissão, frete, transporte, intermediador, parcelas e observações. O operador não precisa abrir o
 * Bling para consultar. O JSON completo fica logo abaixo, recolhido.
 */
function ChannelDataBlock({ order }: { order: PlatformUnifiedOrder }): ReactElement | null {
  const raw = order.blingRaw;
  if (!raw) return null;
  const get = (...path: string[]): unknown => path.reduce<unknown>((acc, key) => (acc && typeof acc === "object" ? (acc as Record<string, unknown>)[key] : undefined), raw);
  const installments = Array.isArray(raw.parcelas) ? (raw.parcelas as Array<Record<string, unknown>>) : [];
  const address = get("transporte", "etiqueta") as Record<string, unknown> | undefined;
  return (
    <section className={`order-detail__channel ${BLOCK_CLASS}`} data-section="dados-do-canal">
      <SectionTitle icon={Receipt}>Dados do canal (Bling)</SectionTitle>
      <p className="m-0 text-xs text-muted">
        {`Guardado no Supabase${order.blingSyncedAt ? `; lido do Bling em ${formatDateTime(order.blingSyncedAt)}` : ""}. Não é preciso consultar o Bling para ver estes dados.`}
      </p>
      <dl className="m-0">
        <Row label="Canal">{order.channelName ?? "—"}</Row>
        <Row label="Número no canal">{show(raw.numeroLoja)}</Row>
        <Row label="Situação no Bling">{order.blingSituation ?? "—"}</Row>
        <Row label="Vendedor / intermediador">{show(get("intermediador", "nomeUsuario"))}</Row>
        <Row label="Comissão do canal">{brlFrom(get("taxas", "taxaComissao"))}</Row>
        <Row label="Custo do frete">{brlFrom(get("taxas", "custoFrete"))}</Row>
        <Row label="Valor base">{brlFrom(get("taxas", "valorBase"))}</Row>
        <Row label="Frete cobrado">{brlFrom(get("transporte", "frete"))}</Row>
        <Row label="Volumes / prazo">{`${show(get("transporte", "quantidadeVolumes"))} / ${show(get("transporte", "prazoEntrega"))} dia(s)`}</Row>
        <Row label="Desconto">{brlFrom(raw.desconto)}</Row>
        <Row label="Outras despesas">{brlFrom(raw.outrasDespesas)}</Row>
        <Row label="Data de saída">{show(raw.dataSaida)}</Row>
        <Row label="Nota fiscal (Bling)">{show(get("notaFiscal", "id"))}</Row>
      </dl>
      {address && address.nome ? (
        <p className="m-0 text-sm text-body" data-delivery-address="true">
          <strong className="text-ink">Entrega: </strong>
          {[address.nome, address.endereco, address.numero, address.complemento, address.bairro, address.municipio, address.uf, address.cep].filter(Boolean).join(", ")}
        </p>
      ) : null}
      {installments.length ? (
        <div>
          <h3 className="mb-1 text-sm font-extrabold text-ink">{`Parcelas (${installments.length})`}</h3>
          <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm" data-installments={installments.length}>
            {installments.map((installment, index) => (
              <li key={index} className="flex justify-between gap-3">
                <span>{`${index + 1}/${installments.length} · vence ${show(installment.dataVencimento)}${installment.observacoes ? ` · ${String(installment.observacoes).replace(/^Método de pagamento:\s*/i, "")}` : ""}`}</span>
                <span className="tabular-nums">{brlFrom(installment.valor)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {raw.observacoes || raw.observacoesInternas ? (
        <p className="m-0 text-sm text-body">{[raw.observacoes, raw.observacoesInternas].filter(Boolean).join(" · ")}</p>
      ) : null}
      <details className="text-xs text-muted">
        <summary className="cursor-pointer font-bold text-ink">Ver todos os dados do canal (JSON)</summary>
        <pre className="mt-2 max-h-96 overflow-auto rounded-control bg-surface-soft p-3" data-channel-raw="true">{JSON.stringify(raw, null, 2)}</pre>
      </details>
    </section>
  );
}

function ItemsBlock({ order }: { order: PlatformUnifiedOrder }): ReactElement {
  return (
    <section className={`order-detail__items ${BLOCK_CLASS}`} data-section="itens">
      <SectionTitle icon={Package}>Itens do pedido</SectionTitle>
      <RecordTable<OrderCartItem>
        caption="Itens do pedido, com o preço travado no momento da venda"
        hideCaption
        rows={order.items}
        idOf={(item) => `${item.productId}:${item.offerId}:${(item as { sku?: string | null }).sku ?? ""}:${(item as { description?: string }).description ?? ""}`}
        emptyMessage="Nenhum item neste pedido."
        columns={[
          {
            key: "product",
            label: "Produto",
            render: (item) => {
              const loose = item as OrderCartItem & { description?: string; sku?: string | null };
              const label = loose.description || item.productId;
              return (
                <span className="flex flex-col" data-order-item={loose.sku ?? item.productId}>
                  {item.productId ? (
                    <Link to={`/catalogo/${item.productId}`} className="font-bold text-ink underline decoration-brand decoration-2 underline-offset-2">
                      {label}
                    </Link>
                  ) : (
                    <span className="font-bold text-ink">{label}</span>
                  )}
                  {loose.sku ? <span className="text-xs text-muted">{`SKU ${loose.sku}`}</span> : null}
                </span>
              );
            }
          },
          { key: "quantity", label: "Qtd.", align: "right", render: (item) => String(item.quantity) },
          { key: "unitPrice", label: "Preço unitário", align: "right", render: (item) => formatBrl(item.unitPriceCents) },
          {
            key: "lineTotal",
            label: "Subtotal",
            align: "right",
            render: (item) => <span className="font-extrabold">{formatBrl(item.unitPriceCents * item.quantity)}</span>
          }
        ]}
      />
    </section>
  );
}

export function PedidoDetalheView({ orders, orderId, transition, acquisition }: PedidoDetalheViewProps): ReactElement {
  if (isOrdersSourceAwaitingContract(orders)) {
    return (
      <div className="page page--pedido-detalhe flex flex-col gap-6" data-page="pedido-detalhe">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">Pedido</h2>
        </header>
        <StatusPanel
          kind="awaiting_source"
          title={`Pedidos unificados ${AWAITING_CONTRACT_LABEL}`}
          detail="A rota /api/platform/orders não respondeu neste ambiente, então não há registro unificado para abrir. O pedido do checkout continua acessível pela lista de pedidos."
        />
      </div>
    );
  }
  const order = unifiedOrderById(orders, orderId);
  if (!order) {
    return (
      <div className="page page--pedido-detalhe flex flex-col gap-6" data-page="pedido-detalhe">
        <header className="drilldown-header flex flex-col gap-1">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">Pedido</h2>
        </header>
        <StatusPanel
          kind="empty"
          title={`Pedido ${orderId} não está na lista unificada`}
          detail="A fonte respondeu e não tem este identificador — dizer isso é melhor que mostrar uma tela vazia."
        />
        <Link to="/pedidos" className="w-fit text-sm font-semibold text-brand-text hover:underline">
          Voltar para a lista de pedidos
        </Link>
      </div>
    );
  }
  return (
    <div className="page page--pedido-detalhe flex flex-col gap-6" data-page="pedido-detalhe">
      <header className="drilldown-header flex flex-col gap-3">
        <Link to="/pedidos" className="drilldown-header__back inline-flex w-fit items-center gap-1 text-sm font-bold text-muted hover:text-ink">
          <ArrowLeft className="h-4 w-4" aria-hidden focusable={false} />
          Voltar para a lista de pedidos
        </Link>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-2xl font-black text-ink sm:text-3xl">{order.channelKind ? `Pedido ${order.externalId}` : `Pedido ${order.id}`}</h2>
          <Badge tone={DETAIL_STATE_TONE[order.state]}>{ORDER_STATE_LABELS[order.state]}</Badge>
          <Badge tone="neutral">{ORDER_ORIGIN_LABELS[order.origin]}</Badge>
        </div>
        <p className="drilldown-header__origin m-0 text-sm text-muted" data-record-id={order.id}>
          {`Registrado em ${formatDateTime(order.placedAt)} · atualizado em ${formatDateTime(order.updatedAt)}`}
        </p>
      </header>
      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-6">
          <ItemsBlock order={order} />
          <ChannelDataBlock order={order} />
          <OrderStateMachine order={order} transition={transition} />
        </div>
        <aside aria-label="Resumo do pedido" className="flex min-w-0 flex-col gap-6">
          <OriginBlock order={order} />
          {/* `key` recria o formulário quando o pedido recarrega com a origem gravada. */}
          <AcquisitionBlock key={`${order.id}:${order.acquisitionOrigin ?? ""}`} order={order} acquisition={acquisition} />
          <PaymentBlock order={order} />
          <FulfillmentBlock order={order} />
          <FiscalBlock order={order} />
          <ClosureBlock order={order} />
        </aside>
      </div>
    </div>
  );
}

export function PedidoDetalhe(): ReactElement {
  const params = useParams();
  const orderId = params.orderId ?? "";
  const mutation = useOrderTransition();
  const origins = useSalesOrigins();
  const acquisitionMutation = useSetOrderAcquisition();
  // Mesmo padrão da edição comercial (Task 11): a view recebe o caminho de
  // gravação por prop, então o teste exercita o MESMO submit sem navegador.
  const transition: OrderTransitionHandle = {
    submit: (input) => mutation.mutate({ orderId, input }),
    pending: mutation.isPending,
    error: mutation.error ?? null,
    savedState: mutation.data?.state ?? null
  };
  // Um pedido só (com o dado completo do canal): o detalhe não depende de o pedido estar no período da lista.
  const query = useOrder(orderId);
  if (query.isPending) return <StatusPanel kind="loading" title="Carregando o pedido…" />;
  if (query.error) return <StatusPanel kind="error" title="Não foi possível carregar o pedido" detail={errorDetail(query.error)} />;
  const orders: OrdersDataset = { orders: query.data ? [query.data] : [], sources: { platformOrders: "live" } };
  // Origem de aquisição: sem o cadastro de origens (rota não publicada) o bloco fica só em leitura.
  const acquisition: OrderAcquisitionHandle | undefined = origins.data
    ? {
        origins: origins.data,
        save: (input) => acquisitionMutation.mutate({ orderId, input }),
        pending: acquisitionMutation.isPending,
        error: acquisitionMutation.error ?? null,
        saved: acquisitionMutation.isSuccess
      }
    : undefined;
  return <PedidoDetalheView orders={orders} orderId={orderId} transition={transition} acquisition={acquisition} />;
}
